import { InputHTMLAttributes, forwardRef, useId, useMemo, useState } from 'react';
import { Controller, get, useFormState } from 'react-hook-form';
import clsx from 'clsx';
import Label from './Label';
import { Skeleton } from '../Elements/Skeletons';
import Error from './Error';

import { fieldWidths } from './Form';

export interface TextInputFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  loading?: boolean;
  label?: string;
  description?: string;
  additionalContainerClasses?: string;
  hideLabel?: boolean;
  casing?: 'uppercase' | 'lowercase' | undefined;
  trimSpaces?: boolean;
  width?: keyof typeof fieldWidths;
  variant?: 'text' | 'integer';
}

const PasswordInput = forwardRef<
  HTMLInputElement,
  Pick<InputHTMLAttributes<HTMLInputElement>, 'type' | 'className' | 'id'> & {
    error: boolean;
  }
>(function PasswordInput({ error, ...props }, ref) {
  const [showPassword, setShowPassword] = useState(false);

  const togglePassword = () => {
    setShowPassword(!showPassword);
  };
  return (
    <div className="relative w-full">
      <input
        type={`${showPassword ? 'text' : 'password'}`}
        ref={ref}
        className={clsx(
          'relative w-full p-2 border rounded-md text-grey-1000 dark:text-blue-100 bg-grey-100 border-grey-500 placeholder:text-grey-500 dark:placeholder:text-grey-400 dark:bg-blue-900 dark:border-grey-600 focus:border-blue-500 dark:focus:border-blue-300 focus:outline-none',
          {
            'border-red-500 focus:border-red-300': error,
          }
        )}
        {...props}
      />
      <button
        className="absolute top-1/4 right-3 material-symbols-outlined dark:text-blue-200"
        onClick={togglePassword}
        type="button"
      >
        {showPassword ? 'visibility_off' : 'visibility'}
      </button>
    </div>
  );
});

type SimpleTextInputFieldProps = Omit<TextInputFieldProps, 'casing' | 'trimspaces'>;

const TextInputFieldInternal = forwardRef<HTMLInputElement, SimpleTextInputFieldProps>(
  function TextInputFieldInternal(
    {
      id,
      loading,
      label,
      description,
      additionalContainerClasses,
      hideLabel,
      type,
      width = 'full',
      ...props
    },
    ref
  ) {
    const { name, disabled } = props;
    const formState = useFormState();
    const error = get(formState.errors, name);

    const defaultId = useId();
    id = id ?? defaultId;

    return (
      <div
        id={id}
        className={clsx(
          'flex flex-col gap-2 w-full',
          additionalContainerClasses,
          fieldWidths[width]
        )}
      >
        {label ? (
          <Label htmlFor={`${id}::input`} hideLabel={hideLabel} description={description}>
            {label}
          </Label>
        ) : null}
        {loading || formState.isLoading ? (
          <Skeleton height="2.5em" />
        ) : type === 'password' ? (
          <PasswordInput ref={ref} {...props} id={`${id}::input`} error={!!error} />
        ) : (
          <input
            id={`${id}::input`}
            ref={ref}
            type={type}
            className={clsx(
              'relative w-full p-2 border rounded-md dis bg-grey-100  border-grey-500 placeholder:text-grey-500 text-grey-1000 dark:text-blue-100 dark:placeholder:text-grey-400 dark:bg-blue-900 dark:border-grey-600 focus:border-blue-500 dark:focus:border-blue-300 focus:outline-none read-only:bg-grey-200 read-only:focus:border-grey-500',
              {
                'bg-grey-300': disabled,
                'border-red-500 focus:border-red-300': !!error,
              }
            )}
            {...props}
          />
        )}
        <Error error={error} />
      </div>
    );
  }
);

const ControlledTextInputField = forwardRef<
  HTMLInputElement,
  SimpleTextInputFieldProps & { transform: (input: string) => string }
>(function ControlledTextInputField({ transform, defaultValue, disabled, ...props }, ref) {
  // See https://www.reddit.com/r/reactjs/comments/13i537b/comment/jk8ltfx/
  return (
    <Controller
      name={props.name ?? ''}
      defaultValue={defaultValue}
      disabled={disabled}
      render={({ field }) => (
        <TextInputField
          {...props}
          name={field.name}
          disabled={field.disabled}
          value={field.value ?? ''}
          onChange={(e) => {
            field.onChange(transform(e.target.value));
          }}
          onBlur={field.onBlur}
          ref={ref}
        />
      )}
    />
  );
});

export const TextInputField = forwardRef<HTMLInputElement, TextInputFieldProps>(
  function TextInputField({ casing, trimSpaces, variant, ...props }, ref) {
    const transform = useMemo(() => {
      if (!casing && !trimSpaces) return;
      return (value: string) => {
        if (trimSpaces) {
          value = value.replace(' ', '');
        }
        if (casing === 'uppercase') {
          value = value.toUpperCase();
        } else if (casing === 'lowercase') {
          value = value.toLowerCase();
        }
        return value;
      };
    }, [casing, trimSpaces]);

    const inputType = variant === 'integer' ? 'number' : props.type;

    return transform ? (
      <ControlledTextInputField {...props} ref={ref} type={inputType} transform={transform} />
    ) : (
      <TextInputFieldInternal {...props} ref={ref} type={inputType} />
    );
  }
);
